/**
 * Wormholes left behind by warping pilots. Each lives WORMHOLE_LIFE_MS;
 * pilots in range can join the jump (F). Positions are system-space so the
 * floating origin can't move them.
 */
import * as THREE from "three";
import { systemDefFor } from "@hyperlight/shared";

export const WORMHOLE_LIFE_MS = 10_000;
export const WORMHOLE_JOIN_RANGE = 2000;

interface Wormhole {
  pos: THREE.Vector3; // system space
  to: string;
  destName: string;
  expiresAt: number;
  group: THREE.Group;
  ring: THREE.Mesh;
  disc: THREE.Mesh;
}

export class WormholeField {
  private holes: Wormhole[] = [];

  constructor(private scene: THREE.Scene) {}

  spawn(p: [number, number, number], to: string): void {
    const destName = systemDefFor(to)?.name ?? "UNKNOWN"; // C4: any id form
    const group = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(9, 0.5, 10, 64),
      new THREE.MeshBasicMaterial({
        color: 0xb08aff,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(8.4, 48),
      new THREE.MeshBasicMaterial({
        color: 0x3a1f66,
        transparent: true,
        opacity: 0.75,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    group.add(ring, disc);
    this.scene.add(group);
    this.holes.push({
      pos: new THREE.Vector3(...p),
      to,
      destName,
      expiresAt: performance.now() + WORMHOLE_LIFE_MS,
      group,
      ring,
      disc,
    });
  }

  /** Animate, expire, and convert to render space. */
  update(time: number, origin: THREE.Vector3): void {
    const now = performance.now();
    for (let i = this.holes.length - 1; i >= 0; i--) {
      const w = this.holes[i];
      const left = w.expiresAt - now;
      if (left <= 0) {
        this.dispose(w);
        this.holes.splice(i, 1);
        continue;
      }
      w.group.position.copy(w.pos).sub(origin);
      w.group.rotation.y = time * 0.7;
      w.group.rotation.x = Math.sin(time * 0.4) * 0.4;
      // Collapse anxiety: shrink + flicker over the last 2.5 s.
      const fade = Math.min(1, left / 2500);
      const s = 0.6 + 0.4 * fade + 0.05 * Math.sin(time * 9);
      w.group.scale.set(s, s, s);
      (w.ring.material as THREE.MeshBasicMaterial).opacity = 0.9 * fade;
      (w.disc.material as THREE.MeshBasicMaterial).opacity = 0.75 * fade;
    }
  }

  /** Nearest joinable wormhole within range of `pos` (system space). */
  nearest(pos: THREE.Vector3): { to: string; destName: string; pos: THREE.Vector3 } | null {
    let best: Wormhole | null = null;
    let bestD = WORMHOLE_JOIN_RANGE * WORMHOLE_JOIN_RANGE;
    for (const w of this.holes) {
      const d = w.pos.distanceToSquared(pos);
      if (d < bestD) {
        bestD = d;
        best = w;
      }
    }
    return best ? { to: best.to, destName: best.destName, pos: best.pos.clone() } : null;
  }

  clear(): void {
    for (const w of this.holes) this.dispose(w);
    this.holes = [];
  }

  private dispose(w: Wormhole): void {
    this.scene.remove(w.group);
    w.ring.geometry.dispose();
    (w.ring.material as THREE.Material).dispose();
    w.disc.geometry.dispose();
    (w.disc.material as THREE.Material).dispose();
  }
}
